import { Tooltip } from '@odo/shared-ui';
import { TOOLTIP_OVER_MODAL } from './DmControlPrimitives';
import { daysFromToday, formatYmd, shortDateInDays } from './dmControlUtils';

const SCAN_COUNT = 6;
/** Room past the last scan, so its label isn't clipped. */
const HORIZON_PADDING = 1.05;
const PAUSE_STRIPES = 'repeating-linear-gradient(135deg,#fde68a 0 4px,#fef9e7 4px 8px)';

export type ScanTimelineProps =
    | { empty: string }
    | {
          /** First scan, yyyy-mm-dd. */
          start: string;
          intervalDays: number;
          /** Paused until this yyyy-mm-dd; the stretch until then is hatched. */
          pausedUntil?: string;
      };

interface ScanMarkProps {
    days: number;
    first: boolean;
    /** The first scan's due date when it has already passed. */
    overdueSince?: string;
    left: string;
}

function ScanMark({ days, first, overdueSince, left }: ScanMarkProps) {
    const dot = overdueSince
        ? 'border-red-500 bg-red-500'
        : `border-[#007E87] ${first ? 'bg-[#007E87]' : 'bg-white'}`;
    const text = overdueSince
        ? 'font-semibold text-red-600'
        : first
          ? 'font-semibold text-gray-800'
          : 'text-gray-500';
    return (
        <div
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
            style={{ left }}
        >
            <span className={`mt-[4px] h-[11px] w-[11px] rounded-full border-2 ${dot}`} />
            <Tooltip
                content={overdueSince && `Was due ${formatYmd(overdueSince)}`}
                className={TOOLTIP_OVER_MODAL}
                focusable
            >
                <span className={`mt-1 whitespace-nowrap text-[11px] tabular-nums ${text}`}>
                    {overdueSince ? 'Overdue' : shortDateInDays(days)}
                </span>
            </Tooltip>
        </div>
    );
}

// The next few scans on a line from today, so a schedule change shows its effect before saving.
// A scan already past due sits on today, and the ones after it follow from there.
export function ScanTimeline(props: ScanTimelineProps) {
    if ('empty' in props) return <p className="py-2 text-[13px] text-gray-500">{props.empty}</p>;
    const overdue = daysFromToday(props.start) < 0;
    const first = Math.max(daysFromToday(props.start), 0);
    const scans = Array.from(
        { length: SCAN_COUNT },
        (_, index) => first + index * props.intervalDays
    );
    const horizon = Math.max(scans[SCAN_COUNT - 1], 1) * HORIZON_PADDING;
    const position = (days: number) => `${(Math.max(0, days) / horizon) * 100}%`;
    const pausedDays = props.pausedUntil ? daysFromToday(props.pausedUntil) : 0;
    return (
        <div className="flex items-start gap-3">
            <span className="w-9 shrink-0 pt-0.5 text-[11px] font-medium text-gray-400">Today</span>
            <div className="relative mr-4 h-10 flex-1">
                <div className="absolute left-0 right-0 top-[9px] h-px bg-gray-200" />
                <span className="absolute left-0 top-[5px] h-[9px] w-px bg-gray-400" />
                {pausedDays > 0 && props.pausedUntil && (
                    <div
                        className="absolute top-[6px] h-[7px] rounded-sm"
                        title={`Paused until ${formatYmd(props.pausedUntil)}`}
                        style={{ left: 0, width: position(pausedDays), background: PAUSE_STRIPES }}
                    />
                )}
                {scans.map((days, index) => (
                    <ScanMark
                        key={index}
                        days={days}
                        first={index === 0}
                        overdueSince={index === 0 && overdue ? props.start : undefined}
                        left={position(days)}
                    />
                ))}
            </div>
        </div>
    );
}
